import { useDropzone } from 'react-dropzone'
import { useEffect, useState } from 'react'
import axios from 'axios'
import LinearProgress from '@mui/material/LinearProgress'
import Box from '@mui/material/Box'
import useReportError from '../../hooks/useReportError'
import { BASE_URL } from '../../constants'
import { getRecaptchaV3Token } from '../../utils/recaptcha'
import RecaptchaModal from '../Recaptcha/RecaptchaModal'

const getColor = props => {
  if (props.isDragAccept) {
    return '#00e676'
  }
  if (props.isDragReject) {
    return '#ff1744'
  }
  if (props.isFocused) {
    return '#2196f3'
  }
  return '#dbd9d9'
}

function Dropbox({ onDrop, setUploadStatus, uploadStatus, setFileList }) {
  let {
    getRootProps,
    getInputProps,
    acceptedFiles,
    open,
    isDragAccept,
    isFocused,
    isDragReject,
  } = useDropzone({
    accept: 'image/*,application/pdf,.doc,.docx',
    onDrop,
    noClick: true,
    noKeyboard: true,
  })

  const [fileProgress, setFileProgress] = useState(0)
  const [_, triggerRerender] = useState(0)
  const { reportError } = useReportError()
  const [pendingFiles, setPendingFiles] = useState(null)

  const dragState = { isDragAccept, isFocused, isDragReject }

  const controller = new AbortController()

  const listWithRemove = acceptedFiles.map((file, index) => (
    <p className="p-2 mb-2 rounded-md bg-[#f9f9f9] drop-shadow-sm" key={index}>
      {file.path} - {(file.size / 1000000).toFixed(2)} mb
      <button
        type="button"
        className="ml-2 cursor-pointer text-red"
        onClick={() => {
          acceptedFiles.splice(index, 1)
          triggerRerender(_ + 1)
        }}
      >
        x
      </button>
    </p>
  ))

  useEffect(() => {
    if (acceptedFiles.length === 0) {
      setUploadStatus('selectFiles')
    }

    if (acceptedFiles.length > 0) {
      setUploadStatus('startUpload')
    }
  }, [acceptedFiles, _])

  const submitFile = async e => {
    e.preventDefault()
    if (acceptedFiles && acceptedFiles.length > 0) {
      // v3-first
      let token
      try {
        token = await getRecaptchaV3Token('upload_files')
      } catch (err) {
        console.error('recaptcha error', err)
        setUploadStatus('error')
        return
      }
      setUploadStatus('uploading')
      const formData = new FormData()

  for (const file of acceptedFiles) formData.append('files', file, file.name)
  // include captcha token for backend verification (structured fields expected)
  formData.append('captcha_token.token', token)
  formData.append('captcha_token.version', 'v3')
  formData.append('captcha_token.provider', '1')

      try {
        const response = await axios.request({
          method: 'POST',
          url: `${BASE_URL}/Marketing/AuthorizationRequest`,
          data: formData,
          onUploadProgress: p => {
            setFileProgress(p.loaded / p.total)
          },
          signal: controller.signal,
        })

        setFileProgress(1)

        if (response.status >= 200 && response.status <= 299) {
          setUploadStatus('success')
          setFileList(acceptedFiles.map(file => file.path))
        } else if (response.status === 400) {
          // need v2 checkbox fallback
          setUploadStatus('selectFiles')
            // show checkbox overlay to obtain v2 token and resubmit
            setPendingFiles(acceptedFiles.slice())
            setUploadStatus('awaitingVerification')
            setShowCheckbox(true)
        } else {
          setUploadStatus('error')
          reportError(response)
        }
      } catch (error) {
        if (axios.isCancel(error)) {
        } else {
          reportError(error)
        }
      }
    }
  }

  const [showCheckbox, setShowCheckbox] = useState(false)

  const handleCheckboxVerify = async token => {
    setShowCheckbox(false)
    const pending = pendingFiles
    if (!pending || pending.length === 0) return
    const formData = new FormData()
    for (const file of pending) formData.append('files', file, file.name)
      formData.append('captcha_token.token', token)
      formData.append('captcha_token.version', 'v2')
      formData.append('captcha_token.provider', '1')
    try {
      const response = await axios.request({
        method: 'POST',
        url: `${BASE_URL}/Marketing/AuthorizationRequest`,
        data: formData,
        onUploadProgress: p => setFileProgress(p.loaded / p.total),
        signal: controller.signal,
      })
      if (response.status >= 200 && response.status <= 299) {
        setUploadStatus('success')
        setFileList(pending.files.map(f => f.path))
      } else {
        setUploadStatus('error')
        reportError(response)
      }
    } catch (err) {
      reportError(err)
    } finally {
      setPendingFiles(null)
    }
  }

  const handleCancel = () => {
    controller.abort()
  }

  return (
    <>
      <section
        className="dropbox"
        style={{
          alignSelf: 'center',
          width: '100%',
        }}
      >
        <div
          className="dropbox"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderWidth: '2px',
            borderColor: `${getColor(dragState)}`,
            borderStyle: 'dashed',
            marginTop: '20px',
            color: 'black',
            outline: 'none',
            transition: 'border 0.24s ease-in-out',
          }}
          {...getRootProps({ isDragAccept, isFocused, isDragReject })}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '30px',
            }}
          >
            <input {...getInputProps()} />
            <img className="w-14 opacity-80" src="/upload.svg" />
            <p className="pb-2 text-xl text-grey">Drag & drop files here</p>
            <p className="text-lg text-grey">or</p>
            <button
              type="button"
              className="p-2 px-4 mt-2 transition-all duration-200 border  shadow-sm bg-white-light border-[#009dff] hover:bg-[#e1f0fa] text-[#009dff]"
              onClick={open}
            >
              Browse Files
            </button>
            {acceptedFiles.length > 0 && (
              <>
                <aside className="p-2 mt-4 overflow-y-auto max-h-24">
                  <p>{listWithRemove}</p>
                </aside>
              </>
            )}
          </div>
          <Box sx={{ width: '100%' }}>
            {fileProgress > 0 && (
              <LinearProgress
                variant="determinate"
                value={fileProgress * 100}
              />
            )}
          </Box>
        </div>
        {acceptedFiles.length > 0 && (
          <>
            <button
              disabled={uploadStatus !== 'startUpload'}
              type="button"
              className={`w-full p-2 px-4 mt-2 transition-all duration-200 border shadow-sm bg-[#007bff] border-blue hover:bg-[#1985fa] text-white-light disabled:bg-lightGrey disabled:text-black-default disabled:cursor-not-allowed ${
                uploadStatus === 'Success!'
                  ? 'disabled:bg-green disabled:text-white'
                  : ''
              }`}
              onClick={submitFile}
            >
              {uploadStatus === 'startUpload'
                ? 'Upload files'
                : uploadStatus === 'uploading'
                ? 'Uploading...'
                : uploadStatus === 'success'
                ? 'Done!'
                : 'Error'}
            </button>
          </>
        )}
      </section>
      {showCheckbox && (
        <RecaptchaModal
          message="Please complete the verification to continue"
          onVerify={token => handleCheckboxVerify(token)}
          onError={() => { setShowCheckbox(false); setUploadStatus('error') }}
          onClose={() => setShowCheckbox(false)}
        />
      )}
    </>
  )
}

export default Dropbox
